import { BaseService } from '../base';
import { GetUsersParams, UsersResponse } from '../users';

export interface Advisory {
  id: string;
  name: string;
  email?: string;
  cnpj?: string;
  responsibleName?: string;
  responsiblePhone?: string;
  responsibleEmail?: string;
  location?: string;
  status: 'active' | 'inactive';
  // API specific fields
  partyCode: string;
  partyName: string;
  partyStatus: string;
  partyType: string;
  partyCategory: string;
  organizationIdNumber?: string;
  entityId: string;
  entityType: string;
  tenantCode: string;
  contactEmail: string;
  contactPhone: string;
  contactName: string;
  contactFullAddress: string;
}

export interface AdvisoriesResponse {
  data: Advisory[];
  meta: {
    pageNumber: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface GetAdvisoriesParams {
  pageNo?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  id?: string;
  partyType?: string;
  sortField?: string;
  sortType?: string;
}

export class AdvisoryService extends BaseService {
  private customBaseUrl = import.meta.env.VITE_API_URL;

  constructor() {
    super();
  }

  async getAdvisories(
    params: GetAdvisoriesParams = {}
  ): Promise<{ success: boolean; data?: AdvisoriesResponse; error?: string }> {
    try {
      const queryParams = new URLSearchParams();

      if (params.pageNo)
        queryParams.append('pageNumber', params.pageNo.toString());
      if (params.pageSize)
        queryParams.append('pageSize', params.pageSize.toString());
      if (params.search) queryParams.append('filter', params.search);
      if (params.status) queryParams.append('status', params.status);
      if (params.id) queryParams.append('filter', params.id);
      if (params.sortField) queryParams.append('sortField', params.sortField);
      if (params.sortType) queryParams.append('sortType', params.sortType);

      const queryString = queryParams.toString();
      const path = `/portal/v1/parties/agencies${queryString ? `?${queryString}` : ''}`;
      const response = await this.get(path, {}, this.customBaseUrl);
      return await this.handleJsonResponse<AdvisoriesResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async getAdvisoriesUsers(
    params: GetUsersParams = {}
  ): Promise<{ success: boolean; data?: UsersResponse; error?: string }> {
    try {
      const queryParams = new URLSearchParams();

      // O endpoint só aceita pageNumber/pageSize/sortField/sortType/filter
      // (forbidNonWhitelisted): qualquer outro parâmetro devolve 400.
      if (params.page)
        queryParams.append('pageNumber', params.page.toString());
      if (params.limit) queryParams.append('pageSize', params.limit.toString());
      if (params.search) queryParams.append('filter', params.search);
      const queryString = queryParams.toString();

      const path = `/portal/v1/parties/agencies/${params.partyCode}/users${queryString ? `?${queryString}` : ''}`;
      const response = await this.get(path, {}, import.meta.env.VITE_API_URL);
      return await this.handleJsonResponse<UsersResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async getAdvisoryById(
    advisoryId: string
  ): Promise<{ success: boolean; data?: Advisory; error?: string }> {
    try {
      const response = await this.get(
        `/portal/v1/parties/agencies/${advisoryId}`,
        {},
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<Advisory>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async createAdvisory(
    advisoryData: Omit<Advisory, 'id'>
  ): Promise<{ success: boolean; data?: Advisory; error?: string }> {
    try {
      const response = await this.post(
        '/portal/v1/parties/agencies',
        advisoryData,
        import.meta.env.VITE_API_URL
      ); //enddpoint ainda nao existe no backend
      return await this.handleJsonResponse<Advisory>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async updateAdvisory(
    advisoryId: string,
    advisoryData: Partial<Advisory>
  ): Promise<{ success: boolean; data?: Advisory; error?: string }> {
    try {
      const response = await this.put(
        `/portal/v1/parties/agencies/${advisoryId}`, //enddpoint ainda nao existe no backend
        advisoryData,
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<Advisory>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async deleteAdvisory(
    advisoryId: string
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await this.delete(
        `/portal/v1/parties/agencies/${advisoryId}`,
        {},
        import.meta.env.VITE_API_URL
      ); //enddpoint ainda nao existe no backend
      return await this.handleJsonResponse(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }
}

export const advisoryService = new AdvisoryService();
